// PRFAQSection.js - Australia
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const PRFAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What is the difference between subclass 189, 190, and 491 visas?',
      answer: 'Subclass 189 is a permanent visa for skilled workers without state sponsorship. Subclass 190 is a permanent visa requiring state nomination (adds 5 points). Subclass 491 is a 5-year provisional visa for regional areas requiring state nomination or family sponsorship (adds 15 points), leading to permanent residency (subclass 191) after 3 years of living and working in regional Australia.'
    },
    {
      question: 'How many points do I need for Australian PR?',
      answer: 'The minimum is 65 points to submit an Expression of Interest (EOI), but this doesn\'t guarantee an invitation. Competitive scores are typically 80-90+ depending on your occupation and the invitation round. Points are awarded for age, English proficiency, work experience, education, and other factors. State nomination adds 5-15 points.'
    },
    {
      question: 'Which English test is accepted for Australian PR?',
      answer: 'Australia accepts IELTS, PTE Academic, TOEFL iBT, OET, and Cambridge English: Advanced (CAE). Minimum requirement is Competent English (IELTS 6.0 each band or equivalent). Higher scores provide more points: Proficient (IELTS 7.0) = 10 points, Superior (IELTS 8.0) = 20 points. Test results must be less than 3 years old.'
    },
    {
      question: 'Do I need a job offer for Australian PR?',
      answer: 'No job offer is required for General Skilled Migration (subclass 189, 190, 491). However, some state nomination streams may require or prefer job offers. Employer-sponsored visas (subclass 186) do require a job offer from an approved Australian employer. Having a job offer can strengthen your application and provide additional points.'
    },
    {
      question: 'What is a skills assessment and how long does it take?',
      answer: 'A skills assessment verifies that your qualifications and experience match Australian standards for your nominated occupation. It\'s conducted by designated assessing authorities specific to each occupation (e.g., Engineers Australia, ACS for IT, VETASSESS for general occupations). Processing times vary from 2-4 months depending on the authority and complexity of your case.'
    },
    {
      question: 'Can I include my family in my PR application?',
      answer: 'Yes, you can include your spouse/partner and dependent children in your application. They will also receive permanent residency. Including your spouse can add up to 10 points if they have competent English and positive skills assessment. Each additional applicant requires additional government fees: AUD $2,320 for adults, AUD $1,160 for children under 18.'
    },
    {
      question: 'Which state is easiest for PR nomination?',
      answer: 'South Australia and Tasmania are generally considered more accessible with lower points requirements (65-70) and broader occupation lists. However, "easiest" depends on your occupation, experience, and willingness to live in regional areas. Each state has unique criteria - we assess which state best matches your profile and provides the highest chance of success.'
    },
    {
      question: 'What happens after I receive PR?',
      answer: 'After receiving your permanent residency visa, you must make your initial entry to Australia before the visa expiry date. Once in Australia, you have full work rights, access to Medicare, can study, and can sponsor eligible family members. You can apply for citizenship after 4 years of residence (including 12 months as PR). Your PR status is permanent but you need a Resident Return Visa (RRV) to re-enter Australia if you travel overseas after 5 years.'
    },
    {
      question: 'How long does the entire PR process take?',
      answer: 'The timeline varies by pathway: Skilled Independent (189) typically takes 12-24 months total, State Nominated (190/491) takes 12-18 months, and Employer Sponsored (186) takes 12-18 months. This includes skills assessment (2-4 months), waiting for invitation (varies), and visa processing (6-12 months). Timelines can vary based on occupation, completeness of application, and Department workload.'
    },
    {
      question: 'What is the success rate for Australian PR applications?',
      answer: 'Success rates are high for complete applications meeting all requirements. Applications with positive skills assessments, sufficient points, and complete documentation typically have 85-90% approval rates. Our clients who follow our guidance and submit well-prepared applications have a 98% success rate. The key factors are ensuring eligibility, meeting points requirements, and providing complete, accurate documentation.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-base font-light">
            Common questions about Australian permanent residency applications
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-[#F5F8FA] rounded-lg shadow-sm overflow-hidden border border-gray-200"
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base font-semibold text-[#202356] pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-[#2542D4]" />
                  ) : (
                    <Plus size={24} className="text-[#2542D4]" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-sm font-light leading-relaxed border-t border-gray-200 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Additional Help */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Still have questions?</strong> Book a free consultation with our immigration experts to discuss your specific situation and get personalized guidance on your Australian PR journey.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PRFAQSection;
